 /**
  * SEO HEADING STRUCTURE - 온라인 작업 공간 (Korean)
  * ================================================
  * 
  * TITLE: 온라인 작업 공간 | CSW | Online Workspace | Productivity Tool | Study Stream
  * META DESCRIPTION: 무료 온라인 작업 공간. 커스텀 배경, 칸반 보드, 포모도로 타이머, 생산성 추적 기능 제공.
  * 
  * H1: "온라인 작업 공간"
  * H2: "생산성을 위한 모든 것", "왜 CSW를 선택해야 할까요?", "생산성을 높일 준비가 되셨나요?"
  */
 
 import { Header } from "@/components/Header";
 import { Footer } from "@/components/Footer";
 import { SEO } from "@/components/SEO";
 import { Button } from "@/components/ui/button";
 import { Card, CardContent } from "@/components/ui/card";
 import { Monitor, Users, Cloud, Zap, CheckCircle2, Globe, ArrowRight } from "lucide-react";
 import { Link } from "react-router-dom";
 
 const features = [
   {
     icon: Monitor,
     title: "배경 커스터마이징",
     description: "기분과 집중력에 맞는 맞춤형 배경으로 작업 공간을 개인화하세요.",
   },
   {
     icon: Users,
     title: "유연한 모드",
     description: "다른 사람들과 함께 공부하거나, 음악과 타이머가 있는 개인 공간을 만드세요.",
   },
   {
     icon: Zap,
     title: "생산성 추적",
     description: "실시간 생산성 인사이트로 진행 상황을 모니터링하고 성과를 확인하세요.",
   },
   {
     icon: CheckCircle2,
     title: "칸반 작업 관리",
     description: "직관적인 칸반 보드로 작업을 정리하세요. 드래그, 드롭, 추적이 간편합니다.",
   },
   {
     icon: Cloud,
     title: "캘린더 알림",
     description: "중요한 마감일을 놓치지 마세요. 알림을 설정하고 캘린더와 동기화하세요.",
   },
   {
     icon: Globe,
     title: "같은 관심사의 사람들 만나기",
     description: "휴식, 공부, 작업 모드에서 다른 사람들과 연결하세요. 집중력을 공유하는 친구를 찾으세요.",
   },
 ];
 
 export default function OnlineWorkspaceKr() {
   return (
     <div className="min-h-screen bg-background">
       <SEO 
         title="온라인 작업 공간 | CSW | Online Workspace | Productivity Tool | Study Stream"
         description="무료 온라인 작업 공간. 커스텀 배경, 칸반 작업 관리, 포모도로 타이머, 생산성 추적 기능을 제공합니다. 집중하며 공부하고 체계적으로 정리하세요."
         keywords="온라인 작업 공간, 가상 작업 공간, 온라인 스터디룸, 생산성 도구, 작업 관리, 포모도로 타이머"
         canonicalUrl="/kr/online-workspace/"
       />
       <Header />
       
       <main>
         {/* Hero Section */}
         <section className="relative min-h-[90vh] flex items-center justify-center overflow-hidden">
           <div className="absolute inset-0 bg-gradient-to-b from-background via-background to-muted" />
           <div className="absolute top-20 left-20 w-96 h-96 bg-primary/20 rounded-full blur-[120px] animate-glow-pulse" />
           <div className="absolute bottom-20 right-20 w-96 h-96 bg-secondary/20 rounded-full blur-[120px] animate-glow-pulse" style={{ animationDelay: '1s' }} />
           
           <div className="container relative z-10 mx-auto px-4 sm:px-6 lg:px-8">
             <div className="grid lg:grid-cols-2 gap-12 items-center">
               <div className="text-left space-y-8 animate-fade-in">
                 <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-card/50 border border-border backdrop-blur-sm">
                   <div className="w-2 h-2 bg-primary rounded-full animate-glow-pulse" />
                   <span className="text-sm font-medium text-muted-foreground">어디서나 일하세요</span>
                 </div>
                 
                 <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl font-bold leading-tight">
                   <span className="text-gradient">온라인 작업 공간</span>
                 </h1>
                 
                 <p className="text-xl sm:text-2xl text-muted-foreground font-light max-w-xl">
                   집중, 협업, 성취를 위해 설계된 가상 생산성 환경입니다.
                 </p>
                 
                 <div className="flex flex-col sm:flex-row gap-4 pt-4">
                   <Button 
                     size="lg" 
                     className="group bg-primary hover:bg-primary/90 text-primary-foreground font-semibold px-8 py-6 text-lg glow-primary transition-all hover:scale-105"
                     asChild
                   >
                     <a href="https://app.csw.live" target="_blank" rel="noopener noreferrer">
                       지금 시작하기
                       <ArrowRight className="ml-2 h-5 w-5 group-hover:translate-x-1 transition-transform" />
                     </a>
                   </Button>
                   <Button 
                     size="lg" 
                     variant="outline"
                     className="border-2 border-border hover:bg-card font-semibold px-8 py-6 text-lg transition-all hover:scale-105"
                     asChild
                   >
                     <a href="https://app.csw.live" target="_blank" rel="noopener noreferrer">
                       무료로 체험하기
                     </a>
                   </Button>
                 </div>
                 
                 <div className="flex items-center gap-8 pt-8">
                   <div>
                     <p className="text-3xl font-bold text-gradient">24/7</p>
                     <p className="text-sm text-muted-foreground">항상 열려있음</p>
                   </div>
                   <div className="w-px h-12 bg-border" />
                   <div>
                     <p className="text-3xl font-bold text-gradient">3가지 모드</p>
                     <p className="text-sm text-muted-foreground">휴식, 공부, 작업</p>
                   </div>
                   <div className="w-px h-12 bg-border" />
                   <div>
                     <p className="text-3xl font-bold text-gradient">무료</p>
                     <p className="text-sm text-muted-foreground">영원히</p>
                   </div>
                 </div>
               </div>
               
               <div className="relative animate-fade-in" style={{ animationDelay: '0.2s' }}>
                 <div className="relative rounded-3xl overflow-hidden border border-border/50 shadow-2xl">
                   <video 
                     src="/videos/productivity-demo.mp4" 
                     autoPlay
                     loop
                     muted
                     playsInline
                     className="w-full h-auto"
                   />
                   <div className="absolute inset-0 bg-gradient-to-t from-background/80 via-transparent to-transparent" />
                 </div>
                 
                 <div className="absolute bottom-8 left-8 right-8 bg-card/90 backdrop-blur-md border border-border rounded-2xl p-6 shadow-xl">
                   <div className="flex items-center justify-between">
                     <div>
                       <p className="text-sm text-muted-foreground mb-1">당신의 가상 오피스</p>
                       <p className="text-xl font-bold text-foreground">어디서나 일하세요</p>
                     </div>
                     <div className="w-12 h-12 rounded-full bg-gradient-to-br from-primary to-secondary flex items-center justify-center">
                       <Monitor className="w-6 h-6 text-primary-foreground" />
                     </div>
                   </div>
                 </div>
               </div>
             </div>
           </div>
         </section>
 
         {/* Features Grid */}
         <section className="py-24 px-4 relative overflow-hidden">
           <div className="absolute inset-0 bg-gradient-to-b from-muted to-background" />
           <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-primary/10 rounded-full blur-[150px]" />
           
           <div className="container mx-auto max-w-6xl relative z-10">
             <div className="text-center mb-16 animate-fade-in">
               <h2 className="text-4xl md:text-5xl font-bold font-display mb-6">
                 <span className="text-gradient">생산성</span>을 위한 모든 것
               </h2>
               <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
                 집중적이고 생산적인 작업을 위한 모든 도구를 갖춘 완벽한 디지털 작업 공간
               </p>
             </div>
 
             <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
               {features.map((feature) => {
                 const Icon = feature.icon;
                 return (
                   <Card
                     key={feature.title}
                     className="group bg-card/50 backdrop-blur-sm border-border/50 hover:border-primary/50 transition-all hover:scale-105 hover:shadow-xl"
                   >
                     <CardContent className="p-8 space-y-4">
                       <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-primary/20 to-secondary/20 flex items-center justify-center group-hover:scale-110 transition-transform">
                         <Icon className="w-6 h-6 text-primary" />
                       </div>
                       
                       <h3 className="text-xl font-semibold text-foreground font-display">
                         {feature.title}
                       </h3>
                       
                       <p className="text-muted-foreground leading-relaxed">
                         {feature.description}
                       </p>
                     </CardContent>
                   </Card>
                 );
               })}
             </div>
           </div>
         </section>
 
         {/* Benefits Section */}
         <section className="py-24 px-4 relative">
           <div className="absolute top-0 right-0 w-96 h-96 bg-secondary/10 rounded-full blur-[120px]" />
           
           <div className="container mx-auto max-w-4xl relative z-10">
             <div className="text-center mb-16">
               <h2 className="text-4xl md:text-5xl font-bold font-display mb-6">
                 왜 CSW <span className="text-gradient">작업 공간</span>을 선택해야 할까요?
               </h2>
               <p className="text-xl text-muted-foreground">
                 유연성, 집중력, 연결을 중요시하는 현대인을 위해 만들어졌습니다
               </p>
             </div>
 
             <div className="grid sm:grid-cols-2 gap-8">
               {[
                 { title: "설치 불필요", desc: "다운로드나 설정 없이 모든 브라우저에서 작동합니다." },
                 { title: "실시간 존재감", desc: "실시간 표시기로 누가 함께 작업하고 있는지 확인하세요." },
                 { title: "방해 없는 환경", desc: "집중을 돕도록 설계된 깔끔하고 미니멀한 환경입니다." },
                 { title: "영원히 무료", desc: "모든 기능에 제한 없이 무료로 완전 접근할 수 있습니다." },
               ].map((item, index) => (
                 <div 
                   key={index}
                   className="p-6 rounded-2xl bg-card/50 backdrop-blur-sm border border-border/50 hover:border-primary/50 transition-all hover:shadow-lg"
                 >
                   <h3 className="text-xl font-semibold text-foreground mb-3 font-display">{item.title}</h3>
                   <p className="text-muted-foreground leading-relaxed">{item.desc}</p>
                 </div>
               ))}
             </div>
           </div>
         </section>
 
         {/* CTA Section */}
         <section className="py-24 px-4 relative overflow-hidden">
           <div className="absolute inset-0 bg-gradient-to-b from-background via-primary/5 to-background" />
           <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[400px] bg-primary/20 rounded-full blur-[150px]" />
           
           <div className="max-w-4xl mx-auto text-center relative z-10">
             <h2 className="text-4xl md:text-5xl font-bold mb-6 font-display">
               <span className="text-gradient">생산성</span>을 높일 준비가 되셨나요?
             </h2>
             <p className="text-xl text-muted-foreground mb-10 max-w-2xl mx-auto">
               수천 명의 전문가, 학생, 크리에이터들이 함께 더 나은 작업을 하고 있습니다.
             </p>
             <Button 
               size="lg" 
               className="group bg-primary hover:bg-primary/90 text-primary-foreground font-semibold px-10 py-7 text-lg glow-primary transition-all hover:scale-105"
               asChild
             >
               <a href="https://app.csw.live" target="_blank" rel="noopener noreferrer">
                 작업 공간 시작하기
                 <ArrowRight className="ml-2 h-5 w-5 group-hover:translate-x-1 transition-transform" />
               </a>
             </Button>
           </div>
         </section>
 
         {/* Related Pages */}
         <section className="py-24 px-4 relative overflow-hidden">
           <div className="absolute inset-0 bg-gradient-to-b from-muted/50 to-background" />
           
           <div className="container mx-auto max-w-6xl relative z-10">
             <div className="text-center mb-12">
               <h2 className="text-4xl md:text-5xl font-bold font-display mb-6">
                 더 많은 <span className="text-gradient">기능</span> 살펴보기
               </h2>
               <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
                 CSW의 다양한 기능을 확인해보세요
               </p>
             </div>
             
             <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
               {[
                 { name: "스터디 스트림", path: "/kr/study-stream/" },
                 { name: "스터디 타이머", path: "/kr/study-timer/" },
                 { name: "포모도로 기법", path: "/kr/pomodoro-technique/" },
                 { name: "함께 공부하기", path: "/kr/study-with-me/" },
               ].map((link) => (
                 <Link
                   key={link.path}
                   to={link.path}
                   className="group p-4 rounded-xl bg-card/50 backdrop-blur-sm border border-border/50 hover:border-primary/50 transition-all hover:scale-105 hover:shadow-lg text-center"
                 >
                   <span className="text-sm font-medium text-foreground group-hover:text-primary transition-colors">
                     {link.name}
                   </span>
                 </Link>
               ))}
             </div>
           </div>
         </section>
       </main>
       
       <Footer />
     </div>
   );
 }